import { Component, computed, forwardRef, inject, input, signal, viewChild } from '@angular/core';
import { AbstractFormControl } from '../abstract-form-control';
import { CheckpointItemDto, CheckpointService, SortDto } from '@artemis-software/wr-api';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { map } from 'rxjs';
import { NbPopoverDirective } from '@nebular/theme';
import { watch } from '@/utils/watch';

@Component({
  standalone: false,
  selector: 'wr-checkpoint-select',
  templateUrl: './checkpoint-select.component.html',
  styleUrls: ['./checkpoint-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CheckpointSelectComponent),
      multi: true,
    },
  ],
})
export class CheckpointSelectComponent extends AbstractFormControl<string> {

  readonly tenantId = input<string | undefined>();

  readonly showEmptyOption = input<boolean>(false);

  readonly popover = viewChild(NbPopoverDirective);

  readonly checkpointService = inject(CheckpointService);

  readonly filterText = signal('');

  readonly allCheckpoints = asyncComputed(() =>
    this.checkpointService.findAll({
      tenantId: this.tenantId(),
      sort: { sort: 'fullNumber', order: SortDto.OrderEnum.Asc },
    }).pipe(
      map(items => items.filter(checkpoint => !checkpoint.deprecated)),
    ),
  );

  readonly filteredCheckpoints = computed(() => {
    const all = this.allCheckpoints() ?? [];
    const text = this.filterText().toLowerCase().trim();
    if (!text) return all;
    return all.filter(checkpoint =>
      checkpoint.name.toLowerCase().includes(text) ||
      `${checkpoint.checkpointGroupNumber}.${checkpoint.number}`.includes(text),
    );
  });

  constructor() {
    super();
    watch([this.tenantId], () => {
      this.value = undefined;
      this.filterText.set('');
    });
  }

  override writeValue(id: string | undefined): void {
    super.writeValue(id);
    if (!id) {
      this.filterText.set('');
      return;
    }
    const found = (this.allCheckpoints() ?? []).find(checkpoint => checkpoint.id === id);
    if (found) {
      this.filterText.set(this.labelFor(found));
    }
  }

  labelFor(checkpoint: CheckpointItemDto): string {
    return `(${checkpoint.checkpointGroupNumber}.${checkpoint.number}) ${checkpoint.name}`;
  }

  onSelectionChange(checkpoint: CheckpointItemDto): void {
    this.filterText.set(this.labelFor(checkpoint));
    this.value = checkpoint.id;
    this.popover()?.hide();
  }

  onClear(): void {
    this.value = undefined;
    this.filterText.set('');
  }

  onAllSelected(): void {
    this.value = undefined;
    this.filterText.set('');
    this.popover()?.hide();
  }
}
